import { useEffect, useRef, useState } from 'react';

import { useDisclosure } from '@chakra-ui/react';

export const POPOVER_Z_INDEX = {
  BACKDROP: 'z-[1499]',
  POPOVER: 'z-[2100]',
} as const;

interface UseHiUserPopoverProps {
  isSidebarOpen: boolean;
}

export function useHiUserPopover({ isSidebarOpen }: UseHiUserPopoverProps) {
  const {
    isOpen: isOptionOpen,
    onToggle: onToggleOption,
    onClose: onCloseOption,
  } = useDisclosure();

  const popoverAnchor = useRef<HTMLDivElement>(null);
  const [popoverRect, setPopoverRect] = useState<DOMRect | null>(null);

  useEffect(() => {
    if (!isSidebarOpen) {
      onCloseOption();
    }
  }, [isSidebarOpen, onCloseOption]);

  useEffect(() => {
    if (!isOptionOpen) return;

    const updateRect = () => {
      if (popoverAnchor.current) {
        setPopoverRect(popoverAnchor.current.getBoundingClientRect());
      }
    };

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCloseOption();
    };

    window.addEventListener('resize', updateRect);
    window.addEventListener('scroll', updateRect, true);
    document.addEventListener('keydown', handleEscape);
    return () => {
      window.removeEventListener('resize', updateRect);
      window.removeEventListener('scroll', updateRect, true);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOptionOpen, onCloseOption]);

  const handleToggle = () => {
    if (!isOptionOpen && popoverAnchor.current) {
      setPopoverRect(popoverAnchor.current.getBoundingClientRect());
    }
    onToggleOption();
  };

  return {
    popoverAnchor,
    popoverRect,
    isOptionOpen,
    onCloseOption,
    handleToggle,
  };
}
